import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';

const HackathonFeedback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { token, user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        rating: 0,
        would_recommend: null,
        features_wanted: '',
        pain_points: '',
        improvements: '',
        next_hackathon_suggestions: '',
        additional_comments: '',
    });

    const [hoveredStar, setHoveredStar] = useState(0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (formData.rating === 0) {
            setError('Please select a rating');
            return;
        }

        if (formData.would_recommend === null) {
            setError('Please indicate if you would recommend this hackathon');
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/hackathon/feedback`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setSuccess(true);
                setTimeout(() => {
                    navigate('/hackathon');
                }, 2000);
            } else {
                const data = await response.json();
                setError(data.detail || 'Failed to submit feedback');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const StarRating = () => {
        const stars = [1, 2, 3, 4, 5];
        return (
            <div className="flex gap-2">
                {stars.map((star) => (
                    <button
                        key={star}
                        type="button"
                        onClick={() => setFormData({ ...formData, rating: star })}
                        onMouseEnter={() => setHoveredStar(star)}
                        onMouseLeave={() => setHoveredStar(0)}
                        className="focus:outline-none transition-transform hover:scale-110"
                    >
                        <svg
                            className={`w-10 h-10 ${
                                star <= (hoveredStar || formData.rating)
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                            />
                        </svg>
                    </button>
                ))}
                {formData.rating > 0 && (
                    <span className="ml-2 text-gray-600 self-center">{formData.rating} / 5</span>
                )}
            </div>
        );
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
                    <p className="text-gray-600 mb-6">Please log in to submit feedback.</p>
                    <button
                        onClick={() => navigate('/login', { state: { from: location } })}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                    <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <svg
                            className="w-10 h-10 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
                    <p className="text-gray-600">Your feedback has been submitted successfully.</p>
                    <p className="text-sm text-gray-500 mt-2">Redirecting to hackathon page...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">Hackathon Feedback</h1>
                    <p className="text-lg text-gray-600">
                        Help us improve future events by sharing your experience
                    </p>
                </div>

                {/* Feedback Form */}
                <div className="bg-white rounded-lg shadow-lg p-8">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <p className="text-red-800 text-sm">{error}</p>
                            </div>
                        )}

                        {/* Rating */}
                        <div>
                            <label className="block text-lg font-semibold text-gray-900 mb-3">
                                How would you rate this hackathon? *
                            </label>
                            <StarRating />
                        </div>

                        {/* Would Recommend */}
                        <div>
                            <label className="block text-lg font-semibold text-gray-900 mb-3">
                                Would you share this hackathon with others? *
                            </label>
                            <div className="flex gap-4">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, would_recommend: true })}
                                    className={`flex-1 py-3 px-6 rounded-lg border-2 font-medium transition-all ${
                                        formData.would_recommend === true
                                            ? 'border-green-500 bg-green-50 text-green-700'
                                            : 'border-gray-300 text-gray-700 hover:border-green-300'
                                    }`}
                                >
                                    Yes, I would recommend it
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, would_recommend: false })}
                                    className={`flex-1 py-3 px-6 rounded-lg border-2 font-medium transition-all ${
                                        formData.would_recommend === false
                                            ? 'border-red-500 bg-red-50 text-red-700'
                                            : 'border-gray-300 text-gray-700 hover:border-red-300'
                                    }`}
                                >
                                    No, I wouldn't
                                </button>
                            </div>
                        </div>

                        {/* Features Wanted */}
                        <div>
                            <label className="block text-lg font-semibold text-gray-900 mb-2">
                                What features would you like to see in future hackathons?
                            </label>
                            <p className="text-sm text-gray-500 mb-3">
                                Tell us about tools, datasets, or functionalities you'd like to have.
                            </p>
                            <textarea
                                value={formData.features_wanted}
                                onChange={(e) =>
                                    setFormData({ ...formData, features_wanted: e.target.value })
                                }
                                rows={4}
                                maxLength={2000}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                placeholder="e.g., More training data, GPU access, collaboration tools..."
                            />
                            <p className="text-xs text-gray-500 mt-1 text-right">
                                {formData.features_wanted.length} / 2000
                            </p>
                        </div>

                        {/* Pain Points */}
                        <div>
                            <label className="block text-lg font-semibold text-gray-900 mb-2">
                                What were your main pain points or challenges?
                            </label>
                            <p className="text-sm text-gray-500 mb-3">
                                Let us know what difficulties you encountered during the event.
                            </p>
                            <textarea
                                value={formData.pain_points}
                                onChange={(e) =>
                                    setFormData({ ...formData, pain_points: e.target.value })
                                }
                                rows={4}
                                maxLength={2000}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                placeholder="e.g., Unclear instructions, technical issues, tight deadlines..."
                            />
                            <p className="text-xs text-gray-500 mt-1 text-right">
                                {formData.pain_points.length} / 2000
                            </p>
                        </div>

                        {/* Improvements */}
                        <div>
                            <label className="block text-lg font-semibold text-gray-900 mb-2">
                                What can we improve for next time?
                            </label>
                            <p className="text-sm text-gray-500 mb-3">
                                Share your suggestions for making future hackathons better.
                            </p>
                            <textarea
                                value={formData.improvements}
                                onChange={(e) =>
                                    setFormData({ ...formData, improvements: e.target.value })
                                }
                                rows={4}
                                maxLength={2000}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                placeholder="e.g., Better documentation, more mentorship, longer timeline..."
                            />
                            <p className="text-xs text-gray-500 mt-1 text-right">
                                {formData.improvements.length} / 2000
                            </p>
                        </div>

                        {/* Next Hackathon Suggestions */}
                        <div>
                            <label className="block text-lg font-semibold text-gray-900 mb-2">
                                What type of hackathon would you like to see next?
                            </label>
                            <p className="text-sm text-gray-500 mb-3">
                                Share your ideas for themes, topics, or formats for future hackathons.
                            </p>
                            <textarea
                                value={formData.next_hackathon_suggestions}
                                onChange={(e) =>
                                    setFormData({ ...formData, next_hackathon_suggestions: e.target.value })
                                }
                                rows={4}
                                maxLength={2000}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                placeholder="e.g., AI/ML focused, Web3/Blockchain, Healthcare tech, Climate change solutions..."
                            />
                            <p className="text-xs text-gray-500 mt-1 text-right">
                                {formData.next_hackathon_suggestions.length} / 2000
                            </p>
                        </div>

                        {/* Additional Comments */}
                        <div>
                            <label className="block text-lg font-semibold text-gray-900 mb-2">
                                Any other comments or feedback?
                            </label>
                            <p className="text-sm text-gray-500 mb-3">
                                Anything else you'd like to share with us.
                            </p>
                            <textarea
                                value={formData.additional_comments}
                                onChange={(e) =>
                                    setFormData({ ...formData, additional_comments: e.target.value })
                                }
                                rows={4}
                                maxLength={2000}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                placeholder="Any other thoughts you'd like to share..."
                            />
                            <p className="text-xs text-gray-500 mt-1 text-right">
                                {formData.additional_comments.length} / 2000
                            </p>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex gap-4 pt-4">
                            <button
                                type="button"
                                onClick={() => navigate('/hackathon')}
                                className="flex-1 py-3 px-6 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 py-3 px-6 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Submitting...' : 'Submit Feedback'}
                            </button>
                        </div>

                        <p className="text-sm text-gray-500 text-center">
                            * Required fields
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default HackathonFeedback;
